import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  Alert,
  LinearProgress,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as RunningIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Replay as ReplayIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { AISystemExecution, ExpertExecutionDetail } from '../../types';
import { executionApi } from '../../services/api';
import FullscreenTextArea from './FullscreenTextArea';
import { formatLoopItem, getLoopIterationLabel, getLoopMetadata } from './runsPresentation';
import {
  evaluatorModelSnapshot,
  formatModelPricing,
  getModelDisplayName,
} from '../../utils/modelPricing';
import { UsageBreakdownPanel } from '../common';

interface ExecutionViewerDialogProps {
  open: boolean;
  onClose: () => void;
  execution: AISystemExecution | null;
  initialExpertId?: number | null;
  onExpertRerun?: (expertId: number, newResult: any) => void;
  onExecutionRetry?: (updatedExecution: AISystemExecution) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  isMobile?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, isMobile, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`execution-tabpanel-${index}`}
      aria-labelledby={`execution-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: isMobile ? 1 : 2 }}>{children}</Box>}
    </div>
  );
}

const ExecutionViewerDialog: React.FC<ExecutionViewerDialogProps> = ({
  open,
  onClose,
  execution: initialExecution,
  initialExpertId = null,
  onExpertRerun,
  onExecutionRetry,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const [tabValue, setTabValue] = useState(0);
  const [expandedExpert, setExpandedExpert] = useState<string | false>(false);
  const [rerunningExpert, setRerunningExpert] = useState<number | null>(null);
  const [retryingExecution, setRetryingExecution] = useState(false);
  const [stoppingExecution, setStoppingExecution] = useState(false);
  const [loadingSystemDetail, setLoadingSystemDetail] = useState(false);
  const [loadingAllExpertDetails, setLoadingAllExpertDetails] = useState(false);
  const [loadingExpertDetails, setLoadingExpertDetails] = useState<Record<string, boolean>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  // Internal state to hold the execution data (for refresh after rerun/retry)
  const [execution, setExecution] = useState<AISystemExecution | null>(initialExecution);

  // Update internal state when prop changes (e.g., different execution selected)
  React.useEffect(() => {
    setExecution(initialExecution);
    setExpandedExpert(false);
    setLoadingSystemDetail(false);
    setLoadingAllExpertDetails(false);
    setLoadingExpertDetails({});
  }, [initialExecution]);

  React.useEffect(() => {
    if (!open || !execution || initialExpertId == null) return;

    const matchingResult = execution.expert_results.find(
      (result) => result.expert_id === initialExpertId
    );

    if (matchingResult) {
      setTabValue(0);
      setExpandedExpert(matchingResult.id);
      if (!matchingResult.detail_loaded) {
        void loadExpertDetail(matchingResult);
      }
    }
  }, [execution, initialExpertId, open]);

  // Helper to refresh execution data from API
  const refreshExecution = async () => {
    if (!execution) return;
    try {
      const updated = await executionApi.getSummary(execution.id);
      setExecution(updated);
    } catch (error) {
      console.error('Failed to refresh execution:', error);
    }
  };

  const mergeExpertDetails = (details: ExpertExecutionDetail[]) => {
    setExecution((current) => {
      if (!current) return current;
      const detailById = new Map(details.map((detail) => [detail.id, detail]));
      return {
        ...current,
        expert_results: current.expert_results.map((result) => {
          const detail = detailById.get(result.id);
          return detail ? { ...result, ...detail, detail_loaded: true } : result;
        }),
      };
    });
  };

  const loadExpertDetail = async (result: ExpertExecutionDetail) => {
    if (!execution || result.detail_loaded || loadingExpertDetails[result.id]) return;

    setLoadingExpertDetails((current) => ({ ...current, [result.id]: true }));
    try {
      const detail = await executionApi.getExpertResult(execution.id, result.id);
      mergeExpertDetails([{ ...detail, detail_loaded: true }]);
    } catch (error: any) {
      console.error('Failed to load expert result details:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to load expert details',
        severity: 'error',
      });
    } finally {
      setLoadingExpertDetails((current) => ({ ...current, [result.id]: false }));
    }
  };

  const loadAllExpertDetails = async () => {
    if (!execution || loadingAllExpertDetails) return;
    if (execution.expert_results.every((result) => result.detail_loaded)) return;

    setLoadingAllExpertDetails(true);
    try {
      const details = await executionApi.getExpertResults(execution.id);
      mergeExpertDetails(details.map((detail) => ({ ...detail, detail_loaded: true })));
    } catch (error: any) {
      console.error('Failed to load expert result details:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to load expert details',
        severity: 'error',
      });
    } finally {
      setLoadingAllExpertDetails(false);
    }
  };

  const loadSystemDetail = async () => {
    if (!execution || execution.detail_loaded || loadingSystemDetail) return;

    setLoadingSystemDetail(true);
    try {
      const detail = await executionApi.getSystemDetail(execution.id);
      setExecution((current) =>
        current
          ? {
              ...current,
              ...detail,
              expert_results: current.expert_results,
              detail_loaded: true,
            }
          : detail
      );
    } catch (error: any) {
      console.error('Failed to load execution system details:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to load system details',
        severity: 'error',
      });
    } finally {
      setLoadingSystemDetail(false);
    }
  };

  if (!execution) return null;

  const handleRerunExpert = async (expertId: number, expertName: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent accordion from toggling
    
    setRerunningExpert(expertId);
    try {
      const response = await executionApi.rerunExpert(execution.id, expertId, {
        skip_cache: true,
        use_original_context: true,
      });

      if (response.success) {
        setSnackbar({
          open: true,
          message: `Expert "${expertName}" rerun successfully`,
          severity: 'success',
        });
        // Refresh execution data to show updated results
        await refreshExecution();
        // Notify parent component about the rerun
        if (onExpertRerun && response.result) {
          onExpertRerun(expertId, response.result);
        }
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to rerun expert',
          severity: 'error',
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Failed to rerun expert',
        severity: 'error',
      });
    } finally {
      setRerunningExpert(null);
    }
  };

  const handleRetryExecution = async () => {
    if (!execution) return;
    
    setRetryingExecution(true);
    try {
      const response = await executionApi.retryExecution(execution.id, {
        skip_cache: true,
      });

      if (response.success && response.execution) {
        setSnackbar({
          open: true,
          message: response.message || `Execution retried successfully. ${response.retried_experts || 0} expert(s) re-run.`,
          severity: 'success',
        });
        // Refresh execution data to show updated results
        await refreshExecution();
        // Notify parent component about the retry
        if (onExecutionRetry && response.execution) {
          onExecutionRetry(response.execution);
        }
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to retry execution',
          severity: 'error',
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Failed to retry execution',
        severity: 'error',
      });
    } finally {
      setRetryingExecution(false);
    }
  };

  const handleStopExecution = async () => {
    if (!execution) return;
    
    setStoppingExecution(true);
    try {
      const response = await executionApi.stopExecution(execution.id);

      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Execution stopped successfully',
          severity: 'success',
        });
        // Refresh execution data to show updated status
        await refreshExecution();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to stop execution',
          severity: 'error',
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Failed to stop execution',
        severity: 'error',
      });
    } finally {
      setStoppingExecution(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1 || newValue === 3) {
      void loadAllExpertDetails();
    }
    if (newValue === 2 || newValue === 3) {
      void loadSystemDetail();
    }
  };

  const handleExpertAccordionChange = (panel: string) => 
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedExpert(isExpanded ? panel : false);
      if (isExpanded) {
        const result = execution?.expert_results.find((item) => item.id === panel);
        if (result && !result.detail_loaded) {
          void loadExpertDetail(result);
        }
      }
    };

  const getStatusIcon = (status: string) => {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED':
      case 'SUCCESS':
      case 'COMPLETE':
      case 'CACHED':
        return <SuccessIcon color="success" />;
      case 'ERROR':
      case 'FAILED':
      case 'FAILURE':
      case 'STOPPED':
        return <ErrorIcon color="error" />;
      case 'RUNNING':
      case 'START':
        return <RunningIcon color="primary" />;
      case 'SKIPPED':
        return <RunningIcon color="warning" />;
      default:
        return <RunningIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED':
      case 'SUCCESS':
      case 'COMPLETE':
      case 'CACHED':
        return 'success';
      case 'ERROR':
      case 'FAILED':
      case 'FAILURE':
      case 'STOPPED':
        return 'error';
      case 'RUNNING':
      case 'START':
        return 'primary';
      case 'SKIPPED':
        return 'warning';
      default:
        return 'default';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatJSON = (obj: any) => {
    try {
      // Create a copy to avoid modifying the original object
      // Handle null/undefined
      if (!obj) return String(obj);
      
      const displayObj = { ...obj };
      
      // Hide large file content in metadata display
      if (displayObj.files && Array.isArray(displayObj.files)) {
        displayObj.files = `[${displayObj.files.length} files displayed separately]`;
      }
      
      return JSON.stringify(displayObj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const calculateExpertDuration = (result: ExpertExecutionDetail, index: number): string => {
    // If metadata has duration, use it
    if (result.metadata?.duration) {
      return `${result.metadata.duration.toFixed(2)}s`;
    }
    
    // Otherwise calculate from timestamps
    if (index === 0) {
      // First expert: from system start to expert completion
      const start = new Date(execution.created_at).getTime();
      const end = new Date(result.created_at).getTime();
      return `${((end - start) / 1000).toFixed(2)}s`;
    } else {
      // Subsequent experts: from previous expert to current
      const prevResult = execution.expert_results[index - 1];
      const start = new Date(prevResult.created_at).getTime();
      const end = new Date(result.created_at).getTime();
      return `${((end - start) / 1000).toFixed(2)}s`;
    }
  };

  const hasActions = (result: ExpertExecutionDetail): boolean => {
    return result.metadata?.action_result !== undefined;
  };

  const getActionSummary = (result: ExpertExecutionDetail): string | null => {
    const actionResult = result.metadata?.action_result;
    if (!actionResult) return null;
    
    const total = actionResult.total_actions || 0;
    const successful = actionResult.successful_actions || 0;
    const failed = actionResult.failed_actions || 0;
    
    if (total === 0) return 'No actions taken';
    return `${successful}/${total} actions successful${failed > 0 ? ` (${failed} failed)` : ''}`;
  };

  const renderFiles = (metadata: any) => {
    if (!metadata || !metadata.files || !Array.isArray(metadata.files) || metadata.files.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Sent Files ({metadata.files.length})
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {metadata.files.map((file: any, index: number) => (
            <Paper key={index} variant="outlined" sx={{ p: 1, maxWidth: 300 }}>
              <Typography variant="caption" display="block" gutterBottom title={file.filename}>
                {file.filename || `File ${index + 1}`}
              </Typography>
              {file.mime_type && file.mime_type.startsWith('image/') && file.base64 ? (
                <img
                  src={`data:${file.mime_type};base64,${file.base64}`}
                  alt={file.filename}
                  style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }}
                />
              ) : (
                <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.100', textAlign: 'center', minWidth: 150 }}>
                  <Typography variant="body2">
                    {file.mime_type || 'Unknown type'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round((file.base64?.length || 0) / 1024)} KB
                  </Typography>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  // Use DB fields if available, fall back to computed values
  const totalCost = execution.total_cost ?? execution.expert_results.reduce(
    (sum, result) => sum + (result.total_cost || 0),
    0
  );

  const totalTokens = execution.total_tokens ?? execution.expert_results.reduce(
    (sum, result) => sum + (result.total_token || 0),
    0
  );

  const computeCost = execution.compute_cost ?? 0;
  const expertsCost = execution.experts_cost ?? (totalCost - computeCost);
  const executionTimeMs = execution.execution_time_ms;
  const cpuTimeMs = execution.cpu_time_ms;
  const peakMemoryBytes = execution.peak_memory_bytes;

  const formatMemory = (bytes: number): string => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  const formatDuration = (ms: number): string => {
    if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${ms.toFixed(0)}ms`;
  };

  const completedExperts = execution.expert_results.filter(
    (r) => {
      const status = r.status?.toUpperCase();
      return status === 'SUCCESS' || status === 'CACHED' || status === 'COMPLETED';
    }
  ).length;

  const totalExperts = execution.metadata?.total_experts || execution.expert_results.length;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ py: isMobile ? 1.5 : 2, px: isMobile ? 2 : 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Execution Details</Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {execution.system_name} • {isMobile ? String(execution.id).slice(0, 8) + '...' : execution.id}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: isMobile ? 1.5 : 2 }}>
        {/* Summary Section */}
        <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 2, mb: 2, bgcolor: 'background.default' }}>
          <Grid container spacing={isMobile ? 1.5 : 2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                {getStatusIcon(execution.status)}
                <Chip
                  label={execution.status_display}
                  color={getStatusColor(execution.status) as any}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Execution Type
              </Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5}>
                {execution.execution_type_display}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Experts Completed
              </Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5}>
                {completedExperts} / {totalExperts}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Experts Cost
              </Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5}>
                ${expertsCost.toFixed(4)}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({totalTokens.toLocaleString()} tokens)
                </Typography>
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Compute Cost
              </Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5}>
                ${computeCost.toFixed(6)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Total Cost
              </Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5} color="primary">
                ${totalCost.toFixed(4)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Started At
              </Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5}>
                {new Date(execution.created_at).toLocaleString()}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Completed At
              </Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5}>
                {execution.completed_at
                  ? new Date(execution.completed_at).toLocaleString()
                  : 'In Progress'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Duration
              </Typography>
              <Typography variant="body2" fontWeight="medium" mt={0.5}>
                {executionTimeMs != null
                  ? formatDuration(executionTimeMs)
                  : execution.completed_at
                  ? `${(
                      (new Date(execution.completed_at).getTime() -
                        new Date(execution.created_at).getTime()) /
                      1000
                    ).toFixed(2)}s`
                  : 'Running...'}
              </Typography>
            </Grid>
            {/* Resource Metrics */}
            {cpuTimeMs != null && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  CPU Time
                </Typography>
                <Typography variant="body2" fontWeight="medium" mt={0.5}>
                  {formatDuration(cpuTimeMs)}
                </Typography>
              </Grid>
            )}
            {peakMemoryBytes != null && peakMemoryBytes > 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Peak Memory
                </Typography>
                <Typography variant="body2" fontWeight="medium" mt={0.5}>
                  {formatMemory(peakMemoryBytes)}
                </Typography>
              </Grid>
            )}
          </Grid>
          <UsageBreakdownPanel
            usage={execution.usage_breakdown}
            compact
            sx={{ mt: 2 }}
          />
        </Paper>

        {execution.status === 'RUNNING' && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
            TabIndicatorProps={{ sx: { display: 'none' } }}
            sx={{
              '& .MuiTab-root': {
                minWidth: isMobile ? 'auto' : 90,
                px: isMobile ? 1.5 : 2,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                borderBottom: '2px solid transparent',
                '&.Mui-selected': {
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                },
              },
            }}
          >
            <Tab label={isMobile ? 'Results' : 'Expert Results'} />
            <Tab label={isMobile ? 'Actions' : 'Actions Summary'} />
            <Tab label={isMobile ? 'Logs' : 'System Logs'} />
            <Tab label={isMobile ? 'Meta' : 'Result & Metadata'} />
          </Tabs>
        </Box>

        {/* Expert Results Tab */}
        <TabPanel value={tabValue} index={0} isMobile={isMobile}>
          {execution.expert_results.length === 0 ? (
            <Alert severity="info">No expert results yet</Alert>
          ) : (
            execution.expert_results.map((result, index) => {
              const loopMetadata = getLoopMetadata(result);
              const loopIterationLabel = getLoopIterationLabel(result);

              return (
              <Accordion
                key={result.id}
                expanded={expandedExpert === result.id}
                onChange={handleExpertAccordionChange(result.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    display="flex"
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    flexDirection={isMobile ? 'column' : 'row'}
                    gap={isMobile ? 1 : 2}
                    width="100%"
                  >
                    <Box display="flex" alignItems="center" gap={1} width={isMobile ? '100%' : 'auto'}>
                      <Typography sx={{ flexShrink: 0 }} fontWeight="medium">
                        #{index + 1}
                      </Typography>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography 
                          fontWeight="medium"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {result.expert_name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Tooltip title={formatModelPricing(result)} arrow>
                            <Typography variant="caption" color="text.secondary">
                              {result.expert_type} • {getModelDisplayName(result, 'No model')}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      gap={0.5}
                      flexWrap="wrap"
                      sx={{ ml: isMobile ? 0 : 'auto' }}
                    >
                    {loopIterationLabel && (
                      <Chip
                        label={loopIterationLabel}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{
                          height: '22px',
                          fontSize: '0.7rem',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    )}
                    {/* Calls Summary Badge - shows action/tool call stats */}
                    {result.calls_summary && result.calls_summary.total > 0 && (
                      <Tooltip title={`${result.calls_summary.successful} successful, ${result.calls_summary.failed} failed`}>
                        <Chip
                          label={
                            result.calls_summary.failed > 0
                              ? `${result.calls_summary.successful}/${result.calls_summary.total} ${isMobile ? '' : (result.calls_summary.type === 'actions' ? 'actions' : 'calls')}`
                              : `${result.calls_summary.total} ${isMobile ? '' : (result.calls_summary.type === 'actions' ? 'actions' : 'calls')}`
                          }
                          size="small"
                          color={result.calls_summary.failed > 0 ? 'warning' : 'info'}
                          variant="outlined"
                          sx={{ 
                            height: '22px', 
                            fontSize: '0.7rem',
                            '& .MuiChip-label': { px: 1 }
                          }}
                          icon={
                            result.calls_summary.failed > 0 
                              ? <ErrorIcon sx={{ fontSize: '14px !important' }} /> 
                              : <SuccessIcon sx={{ fontSize: '14px !important' }} />
                          }
                        />
                      </Tooltip>
                    )}
                    {/* Evaluator Badge - shows accuracy/hallucination scores */}
                    {result.evaluator_status === 'success' && result.evaluator_result && (
                      <Tooltip title={`Accuracy: ${result.evaluator_result.accuracy_percent ?? 'N/A'}% | Hallucination: ${result.evaluator_result.hallucination_percent ?? 'N/A'}%`}>
                        <Chip
                          label={`${result.evaluator_result.accuracy_percent ?? '?'}%`}
                          size="small"
                          color={
                            (result.evaluator_result.accuracy_percent ?? 0) >= 70 &&
                            (result.evaluator_result.hallucination_percent ?? 100) <= 30
                              ? 'success'
                              : (result.evaluator_result.accuracy_percent ?? 0) >= 50
                                ? 'warning'
                                : 'error'
                          }
                          variant="outlined"
                          sx={{ 
                            height: '22px', 
                            fontSize: '0.7rem',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      </Tooltip>
                    )}
                    <Chip
                      label={result.status}
                      color={getStatusColor(result.status) as any}
                      size="small"
                    />
                    {!isMobile && (
                      <Typography variant="caption" color="text.secondary">
                        {calculateExpertDuration(result, index)}
                      </Typography>
                    )}
                    {!isMobile && (
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: '60px', textAlign: 'right' }}>
                        ${((result.total_cost || 0) - (result.compute_cost || 0)).toFixed(4)}
                      </Typography>
                    )}
                    {!isMobile && result.compute_cost != null && result.compute_cost > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: '60px', textAlign: 'right' }}>
                        CPU: ${result.compute_cost.toFixed(4)}
                      </Typography>
                    )}
                    </Box>
                    <Tooltip title="Rerun this expert">
                      <IconButton
                        size="small"
                        onClick={(e) => handleRerunExpert(result.expert_id, result.expert_name, e)}
                        disabled={rerunningExpert === result.expert_id}
                        sx={{ ml: isMobile ? 0 : 1 }}
                      >
                        {rerunningExpert === result.expert_id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <ReplayIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: isMobile ? 1.5 : 2 }}>
                  {loadingExpertDetails[result.id] && !result.detail_loaded ? (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1} py={3}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Loading expert details...
                      </Typography>
                    </Box>
                  ) : !result.detail_loaded ? (
                    <Alert severity="info">
                      Detailed prompt, output, logs, and metadata are loaded on demand.
                      <Button
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => loadExpertDetail(result)}
                      >
                        Load Details
                      </Button>
                    </Alert>
                  ) : (
                  <Box>
                    {/* Expert Details Grid */}
                    <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Tokens Used
                        </Typography>
                        <Typography variant="body2">
                          {(result.total_token || 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Model Snapshot
                        </Typography>
                        <Typography variant="body2">
                          {getModelDisplayName(result, 'No model')}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Input / Output Price
                        </Typography>
                        <Typography variant="body2">
                          {formatModelPricing(result)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Duration
                        </Typography>
                        <Typography variant="body2">
                          {calculateExpertDuration(result, index)}
                        </Typography>
                      </Grid>
                      {loopMetadata && (
                        <>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                              Loop Iteration
                            </Typography>
                            <Typography variant="body2">
                              {loopIterationLabel || '-'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                              Loop Source
                            </Typography>
                            <Typography variant="body2">
                              {loopMetadata.source || 'Not recorded'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                              Loop Item
                            </Typography>
                            <Tooltip title={formatLoopItem(loopMetadata.item, 500)}>
                              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                {loopMetadata.item_alias ? `${loopMetadata.item_alias}: ` : ''}
                                {formatLoopItem(loopMetadata.item)}
                              </Typography>
                            </Tooltip>
                          </Grid>
                        </>
                      )}
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Cached
                        </Typography>
                        <Typography variant="body2">
                          {result.cache_key ? 'Yes' : 'No'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Cost
                        </Typography>
                        <Typography variant="body2">
                          ${((result.total_cost || 0) - (result.compute_cost || 0)).toFixed(4)}
                        </Typography>
                      </Grid>
                      {/* CODE expert compute cost */}
                      {result.compute_cost != null && (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Compute Cost
                          </Typography>
                          <Typography variant="body2">
                            ${result.compute_cost.toFixed(6)}
                          </Typography>
                        </Grid>
                      )}
                      {result.peak_memory_bytes != null && result.peak_memory_bytes > 0 && (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Peak Memory
                          </Typography>
                          <Typography variant="body2">
                            {formatMemory(result.peak_memory_bytes)}
                          </Typography>
                        </Grid>
                      )}
                      {result.cpu_time_ms != null && (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            CPU Time
                          </Typography>
                          <Typography variant="body2">
                            {formatDuration(result.cpu_time_ms)}
                          </Typography>
                        </Grid>
                      )}
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Execution Time
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {new Date(result.created_at).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                    <UsageBreakdownPanel
                      usage={result.usage_breakdown}
                      title="Token cost breakdown"
                      compact
                      sx={{ mb: 2 }}
                    />

                    {/* Actions Summary - Show if expert executed actions */}
                    {hasActions(result) && (
                      <Box sx={{ mb: 2 }}>
                        <Alert 
                          severity={
                            result.metadata?.action_result?.success ? 'success' : 
                            result.metadata?.action_result?.failed_actions > 0 ? 'warning' : 'info'
                          }
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="subtitle2" gutterBottom>
                            Actions Executed
                          </Typography>
                          <Typography variant="body2">
                            {getActionSummary(result)}
                          </Typography>
                          {result.metadata?.action_result?.actions && 
                           result.metadata.action_result.actions.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {result.metadata.action_result.actions.map((action: any, idx: number) => (
                                  <Paper
                                    key={idx}
                                    variant="outlined"
                                    sx={{
                                      p: 1.5,
                                      bgcolor: action.success ? 'success.50' : 'error.50',
                                      borderColor: action.success ? 'success.main' : 'error.main',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight="medium">
                                          {action.success ? '✓' : '✗'} {action.message || 'Action executed'}
                                        </Typography>
                                        
                                        {/* Show action details if available */}
                                        {action.data && (
                                          <Box sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="text.secondary" component="div">
                                              {typeof action.data === 'object' ? (
                                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                  {Object.entries(action.data).map(([key, value]: [string, any]) => {
                                                    // Skip nested objects or arrays for cleaner display
                                                    if (typeof value === 'object' && value !== null) return null;
                                                    return (
                                                      <li key={key}>
                                                        <strong>{key}:</strong> {String(value)}
                                                      </li>
                                                    );
                                                  })}
                                                </Box>
                                              ) : (
                                                String(action.data)
                                              )}
                                            </Typography>
                                          </Box>
                                        )}
                                        
                                        {/* Show error if failed */}
                                        {!action.success && action.error && (
                                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            Error: {action.error}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </Paper>
                              ))}
                            </Box>
                          )}
                        </Alert>
                      </Box>
                    )}

                    {/* Agent Tool Calls - Show for AGENT type experts */}
                    {result.expert_type === 'AGENT' && result.metadata?.tool_calls && result.metadata.tool_calls.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Alert severity="info" icon={false} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2">
                              Agent Tool Calls ({result.metadata.tool_calls.length})
                            </Typography>
                            <Chip 
                              size="small" 
                              label="AGENT" 
                              color="primary" 
                              variant="outlined"
                              sx={{ height: '20px' }}
                            />
                            {result.metadata.agent_name && (
                              <Chip 
                                size="small" 
                                label={result.metadata.agent_name} 
                                color="default" 
                                variant="outlined"
                                sx={{ height: '20px' }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {result.metadata.tool_calls.map((toolCall: any, idx: number) => (
                              <Paper
                                key={idx}
                                variant="outlined"
                                sx={{ p: 1.5, bgcolor: isDark ? 'grey.900' : 'grey.50' }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip 
                                      size="small" 
                                      label={`Step ${toolCall.step_number}`} 
                                      color="default"
                                      sx={{ height: '20px', fontSize: '0.7rem' }}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                      {toolCall.tool_name}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {toolCall.duration_ms > 0 && (
                                      <Chip 
                                        size="small" 
                                        label={`${toolCall.duration_ms}ms`} 
                                        variant="outlined" 
                                        sx={{ height: '18px', fontSize: '0.65rem' }}
                                      />
                                    )}
                                    {toolCall.tokens_used > 0 && (
                                      <Chip 
                                        size="small" 
                                        label={`${toolCall.tokens_used} tokens`} 
                                        variant="outlined"
                                        sx={{ height: '18px', fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                
                                {/* Tool Input */}
                                {toolCall.tool_input && Object.keys(toolCall.tool_input).length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                      Input:
                                    </Typography>
                                    <Paper 
                                      variant="outlined" 
                                      sx={{ p: 1, mt: 0.5, bgcolor: 'background.paper', maxHeight: 120, overflow: 'auto' }}
                                    >
                                      <pre style={{ margin: 0, fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(toolCall.tool_input, null, 2)}
                                      </pre>
                                    </Paper>
                                  </Box>
                                )}
                                
                                {/* Tool Output */}
                                {toolCall.tool_output && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                      Output:
                                    </Typography>
                                    {/* Render image preview if tool output contains file attachment */}
                                    {toolCall.tool_output?.data?._is_file_attachment && 
                                     toolCall.tool_output?.data?.base64 && 
                                     toolCall.tool_output?.data?.mime_type?.startsWith('image/') && (
                                      <Paper
                                        variant="outlined"
                                        sx={{
                                          p: 1,
                                          mt: 0.5,
                                          mb: 1,
                                          bgcolor: '#f0f7ff',
                                          textAlign: 'center',
                                        }}
                                      >
                                        <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                          📊 Chart Image
                                        </Typography>
                                        <img 
                                          src={`data:${toolCall.tool_output.data.mime_type};base64,${toolCall.tool_output.data.base64}`}
                                          alt={toolCall.tool_output.data.filename || 'Chart Image'}
                                          style={{ 
                                            maxWidth: '100%', 
                                            maxHeight: '200px', 
                                            borderRadius: '4px',
                                            border: '1px solid #e0e0e0',
                                          }}
                                        />
                                        {toolCall.tool_output.data.symbol && toolCall.tool_output.data.timeframe && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {toolCall.tool_output.data.symbol} / {toolCall.tool_output.data.timeframe}
                                          </Typography>
                                        )}
                                      </Paper>
                                    )}
                                    <Paper 
                                      variant="outlined" 
                                      sx={{ p: 1, mt: 0.5, bgcolor: 'background.paper', maxHeight: 120, overflow: 'auto' }}
                                    >
                                      <pre style={{ margin: 0, fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                                        {typeof toolCall.tool_output === 'string' 
                                          ? toolCall.tool_output 
                                          : JSON.stringify(
                                              toolCall.tool_output?.data?.base64 
                                                ? { 
                                                    ...toolCall.tool_output, 
                                                    data: { 
                                                      ...toolCall.tool_output.data, 
                                                      base64: `[${toolCall.tool_output.data.base64.length} chars]` 
                                                    } 
                                                  }
                                                : toolCall.tool_output, 
                                              null, 
                                              2
                                            )}
                                      </pre>
                                    </Paper>
                                  </Box>
                                )}
                              </Paper>
                            ))}
                          </Box>
                        </Alert>
                      </Box>
                    )}

                    {/* Agent Execution Info - Show for AGENT type experts */}
                    {result.expert_type === 'AGENT' && result.metadata?.delegated_execution && (
                      <Box sx={{ mb: 2 }}>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: isDark ? 'grey.900' : 'grey.50' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Agent Execution Info</Typography>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">Agent</Typography>
                              <Typography variant="body2">{result.metadata.agent_name}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">Status</Typography>
                              <Box>
                                <Chip 
                                  size="small" 
                                  label={result.metadata.agent_status} 
                                  color={result.metadata.agent_status === 'COMPLETED' ? 'success' : 'error'}
                                  sx={{ height: '20px' }}
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">Total Steps</Typography>
                              <Typography variant="body2">{result.metadata.total_steps || 'N/A'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">Tool Calls</Typography>
                              <Typography variant="body2">{result.metadata.tool_calls?.length || 0}</Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Box>
                    )}

                    {/* Evaluator Results - Show when evaluator was run */}
                    {result.evaluator_status && result.evaluator_status !== '' && (
                      <Box sx={{ mb: 2 }}>
                        <Alert 
                          severity={
                            result.evaluator_status === 'success' 
                              ? (result.evaluator_result?.accuracy_percent && result.evaluator_result.accuracy_percent >= 70 
                                  && result.evaluator_result?.hallucination_percent !== undefined 
                                  && result.evaluator_result.hallucination_percent <= 30
                                  ? 'success' 
                                  : 'warning')
                              : 'error'
                          }
                          icon={false}
                          sx={{ mb: 1 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2">
                              Evaluator Results
                            </Typography>
                            <Chip 
                              size="small" 
                              label={result.evaluator_status} 
                              color={result.evaluator_status === 'success' ? 'success' : 'error'}
                              variant="outlined"
                              sx={{ height: '20px' }}
                            />
                            {result.evaluator_retry_count > 0 && (
                              <Chip 
                                size="small" 
                                label={`${result.evaluator_retry_count} retries`} 
                                color="warning"
                                variant="outlined"
                                sx={{ height: '20px' }}
                              />
                            )}
                          </Box>
                          
                          {result.evaluator_status === 'success' && result.evaluator_result && (
                            <>
                              {/* Accuracy and Hallucination Scores */}
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid size={{ xs: 6, sm: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Accuracy Score
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography 
                                      variant="h6" 
                                      sx={{ 
                                        color: (result.evaluator_result.accuracy_percent ?? 0) >= 70 
                                          ? 'success.main' 
                                          : (result.evaluator_result.accuracy_percent ?? 0) >= 50 
                                            ? 'warning.main' 
                                            : 'error.main',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {result.evaluator_result.accuracy_percent ?? 'N/A'}%
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 6, sm: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Hallucination Score
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography 
                                      variant="h6" 
                                      sx={{ 
                                        color: (result.evaluator_result.hallucination_percent ?? 100) <= 30 
                                          ? 'success.main' 
                                          : (result.evaluator_result.hallucination_percent ?? 100) <= 50 
                                            ? 'warning.main' 
                                            : 'error.main',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {result.evaluator_result.hallucination_percent ?? 'N/A'}%
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 6, sm: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Evaluator Cost
                                  </Typography>
                                  <Typography variant="body2">
                                    ${(result.evaluator_cost || 0).toFixed(4)}
                                  </Typography>
                                </Grid>
                                <Grid size={{ xs: 6, sm: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Evaluator Tokens
                                  </Typography>
                                  <Typography variant="body2">
                                    {(result.evaluator_tokens || 0).toLocaleString()}
                                  </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Evaluator Model Snapshot
                                  </Typography>
                                  <Typography variant="body2">
                                    {getModelDisplayName(evaluatorModelSnapshot(result), 'N/A')}
                                  </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Evaluator Input / Output Price
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatModelPricing(evaluatorModelSnapshot(result))}
                                  </Typography>
                                </Grid>
                              </Grid>
                              <UsageBreakdownPanel
                                usage={result.evaluator_usage_breakdown}
                                title="Evaluator usage"
                                compact
                                sx={{ mt: 1 }}
                              />

                              {/* Reasoning */}
                              {result.evaluator_result.reasoning && (
                                <Paper 
                                  variant="outlined" 
                                  sx={{ p: 1.5, bgcolor: 'background.paper' }}
                                >
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                    Evaluator Reasoning:
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                    {result.evaluator_result.reasoning}
                                  </Typography>
                                </Paper>
                              )}
                            </>
                          )}
                          
                          {result.evaluator_status === 'error' && (
                            <Typography variant="body2" color="error">
                              Evaluator failed to assess this expert's output.
                            </Typography>
                          )}
                        </Alert>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Sent Files Section */}
                    {renderFiles(result.metadata)}

                    {/* Prompt Section */}
                    {result.prompt && (
                      <FullscreenTextArea
                        title="Prompt Sent to LLM"
                        content={result.prompt}
                        maxHeight="200px"
                        format="text"
                      />
                    )}

                    {/* Raw Output Section */}
                    {result.raw_output && (
                      <FullscreenTextArea
                        title="LLM Response (Raw)"
                        content={result.raw_output}
                        maxHeight="300px"
                        format="text"
                      />
                    )}

                    {/* Parsed Output Section */}
                    {result.parsed_output &&
                      Object.keys(result.parsed_output).length > 0 && (
                        <FullscreenTextArea
                          title="Parsed Output (JSON)"
                          content={formatJSON(result.parsed_output)}
                          maxHeight="300px"
                          format="json"
                        />
                      )}

                    {/* Logs Section */}
                    {result.logs && (
                      <FullscreenTextArea
                        title="Execution Logs"
                        content={result.logs}
                        maxHeight="200px"
                        format="text"
                      />
                    )}
                  </Box>
                  )}
                </AccordionDetails>
              </Accordion>
              );
            })
          )}
        </TabPanel>

        {/* Actions Summary Tab */}
        <TabPanel value={tabValue} index={1} isMobile={isMobile}>
          {loadingAllExpertDetails ? (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} py={3}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading action details...
              </Typography>
            </Box>
          ) : (() => {
            // Collect all actions from all expert results
            const allActions: Array<{
              expertName: string;
              expertType: string;
              action: any;
              index: number;
            }> = [];

            execution.expert_results.forEach((result) => {
              if (result.metadata?.action_result?.actions) {
                result.metadata.action_result.actions.forEach((action: any, idx: number) => {
                  allActions.push({
                    expertName: result.expert_name,
                    expertType: result.expert_type,
                    action: action,
                    index: idx,
                  });
                });
              }
            });

            if (allActions.length === 0) {
              return (
                <Alert severity="info">
                  No actions were executed in this system run
                </Alert>
              );
            }

            return (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Total Actions Executed: {allActions.length}
                  </Typography>
                  <Typography variant="body2">
                    Successful: {allActions.filter(a => a.action.success).length} • 
                    Failed: {allActions.filter(a => !a.action.success).length}
                  </Typography>
                </Alert>

                {allActions.map((item, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: item.action.success ? 'success.50' : 'error.50',
                      borderColor: item.action.success ? 'success.main' : 'error.main',
                      borderWidth: 2,
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        label={item.expertName}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={item.expertType}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={item.action.success ? 'Success' : 'Failed'}
                        size="small"
                        color={item.action.success ? 'success' : 'error'}
                      />
                    </Box>

                    {/* Message */}
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                      {item.action.success ? '✓' : '✗'} {item.action.message || 'Action executed'}
                    </Typography>

                    {/* Action Details */}
                    {item.action.data && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Action Details:
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: 'background.paper',
                          }}
                        >
                          <Box component="dl" sx={{ m: 0 }}>
                            {Object.entries(item.action.data).map(([key, value]: [string, any]) => {
                              // Skip complex nested objects
                              if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                                return null;
                              }
                              
                              return (
                                <Box key={key} sx={{ mb: 1, display: 'flex', gap: 1 }}>
                                  <Typography
                                    component="dt"
                                    variant="caption"
                                    fontWeight="bold"
                                    color="text.secondary"
                                    sx={{ minWidth: 120 }}
                                  >
                                    {key}:
                                  </Typography>
                                  <Typography
                                    component="dd"
                                    variant="caption"
                                    sx={{ m: 0, wordBreak: 'break-word' }}
                                  >
                                    {Array.isArray(value) ? value.join(', ') : String(value)}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Paper>
                      </Box>
                    )}

                    {/* Error Details */}
                    {!item.action.success && item.action.error && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Error:</strong> {item.action.error}
                        </Typography>
                        {item.action.error_type && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            Type: {item.action.error_type}
                          </Typography>
                        )}
                      </Alert>
                    )}
                  </Paper>
                ))}
              </Box>
            );
          })()}
        </TabPanel>

        {/* System Logs Tab */}
        <TabPanel value={tabValue} index={2} isMobile={isMobile}>
          {loadingSystemDetail && <LinearProgress sx={{ mb: 2 }} />}
          <FullscreenTextArea
            title="System Execution Logs"
            content={
              loadingSystemDetail && !execution.detail_loaded
                ? 'Loading logs...'
                : execution.logs || 'No logs available'
            }
            maxHeight={isMobile ? '300px' : '500px'}
            format="text"
          />
        </TabPanel>

        {/* Result & Metadata Tab */}
        <TabPanel value={tabValue} index={3} isMobile={isMobile}>
          <Box>
            {(loadingSystemDetail || loadingAllExpertDetails) && <LinearProgress sx={{ mb: 2 }} />}
            {/* Result Section */}
            <FullscreenTextArea
              title="System Result"
              content={
                loadingSystemDetail && !execution.detail_loaded
                  ? 'Loading result...'
                  : execution.result || 'No result available'
              }
              maxHeight={isMobile ? '200px' : '300px'}
              format="text"
            />

            {/* Actions Metadata Section */}
            {(() => {
              const expertResultsWithActions = execution.expert_results.filter(
                r => r.metadata?.action_result
              );
              
              const expertResultsWithToolCalls = execution.expert_results.filter(
                r => r.metadata?.tool_calls && r.metadata.tool_calls.length > 0
              );
              
              if (expertResultsWithActions.length > 0 || expertResultsWithToolCalls.length > 0) {
                return (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                      Actions/Tool Calls Summary
                    </Typography>
                    
                    {/* ACTION expert results */}
                    {expertResultsWithActions.map((result, idx) => (
                      <Paper
                        key={`action-${idx}`}
                        variant="outlined"
                        sx={{ p: 2, bgcolor: 'info.50', mb: 2 }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {result.expert_name} - Action Results
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip
                            label={`Total: ${result.metadata.action_result.total_actions || 0}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`Success: ${result.metadata.action_result.successful_actions || 0}`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                          <Chip
                            label={`Failed: ${result.metadata.action_result.failed_actions || 0}`}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        </Box>

                        {result.metadata.action_result.actions && 
                         result.metadata.action_result.actions.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            {result.metadata.action_result.actions.map((action: any, actionIdx: number) => (
                              <Paper
                                key={actionIdx}
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  bgcolor: 'background.paper',
                                  border: '1px solid',
                                  borderColor: action.success ? 'success.light' : 'error.light',
                                }}
                              >
                                <Typography variant="body2" fontWeight="medium">
                                  {action.success ? '✓' : '✗'} {action.message}
                                </Typography>
                                
                                {action.data && (
                                  <Box
                                    component="pre"
                                    sx={{
                                      mt: 1,
                                      p: 1,
                                      bgcolor: isDark ? 'grey.800' : 'grey.100',
                                      borderRadius: 1,
                                      fontSize: '0.75rem',
                                      overflow: 'auto',
                                      maxHeight: '200px',
                                    }}
                                  >
                                    {formatJSON(action.data)}
                                  </Box>
                                )}

                                {action.error && (
                                  <Alert severity="error" sx={{ mt: 1 }}>
                                    {action.error}
                                  </Alert>
                                )}
                              </Paper>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    ))}
                    
                    {/* AGENT expert results with tool calls */}
                    {expertResultsWithToolCalls.map((result, idx) => (
                      <Paper
                        key={`tool-${idx}`}
                        variant="outlined"
                        sx={{ p: 2, bgcolor: 'primary.50', mb: 2 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {result.expert_name} - Tool Calls
                          </Typography>
                          <Chip 
                            label="AGENT" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ height: '20px' }}
                          />
                          {result.metadata.agent_name && (
                            <Chip 
                              label={result.metadata.agent_name} 
                              size="small" 
                              variant="outlined"
                              sx={{ height: '20px' }}
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip
                            label={`Total: ${result.metadata.tool_calls.length}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          {result.metadata.tool_calls.map((toolCall: any, toolIdx: number) => (
                            <Paper
                              key={toolIdx}
                              sx={{
                                p: 1.5,
                                mb: 1,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'grey.300',
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip 
                                    size="small" 
                                    label={`Step ${toolCall.step_number}`} 
                                    color="default"
                                    sx={{ height: '20px', fontSize: '0.7rem' }}
                                  />
                                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                                    🔧 {toolCall.tool_name}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {toolCall.duration_ms > 0 && (
                                    <Chip 
                                      size="small" 
                                      label={`${toolCall.duration_ms}ms`} 
                                      variant="outlined" 
                                      sx={{ height: '18px', fontSize: '0.65rem' }}
                                    />
                                  )}
                                  {toolCall.tokens_used > 0 && (
                                    <Chip 
                                      size="small" 
                                      label={`${toolCall.tokens_used} tokens`} 
                                      variant="outlined"
                                      sx={{ height: '18px', fontSize: '0.65rem' }}
                                    />
                                  )}
                                  {toolCall.cost > 0 && (
                                    <Chip 
                                      size="small" 
                                      label={`$${toolCall.cost.toFixed(4)}`} 
                                      variant="outlined"
                                      sx={{ height: '18px', fontSize: '0.65rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              
                              {toolCall.tool_input && Object.keys(toolCall.tool_input).length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                    Input:
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      mt: 0.5,
                                      p: 1,
                                      bgcolor: isDark ? 'grey.800' : 'grey.100',
                                      borderRadius: 1,
                                      fontSize: '0.7rem',
                                      overflow: 'auto',
                                      maxHeight: '120px',
                                    }}
                                  >
                                    {formatJSON(toolCall.tool_input)}
                                  </Box>
                                </Box>
                              )}
                              
                              {toolCall.tool_output && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                    Output:
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      mt: 0.5,
                                      p: 1,
                                      bgcolor: isDark ? 'grey.800' : 'grey.100',
                                      borderRadius: 1,
                                      fontSize: '0.7rem',
                                      overflow: 'auto',
                                      maxHeight: '120px',
                                    }}
                                  >
                                    {typeof toolCall.tool_output === 'string' 
                                      ? toolCall.tool_output 
                                      : formatJSON(toolCall.tool_output)}
                                  </Box>
                                </Box>
                              )}
                            </Paper>
                          ))}
                        </Box>
                      </Paper>
                    ))}
                  </>
                );
              }
              return null;
            })()}

            {/* Metadata Section */}
            {execution.metadata && Object.keys(execution.metadata).length > 0 && (
              <Box sx={{ mt: 3 }}>
                <FullscreenTextArea
                  title="System Execution Metadata"
                  content={formatJSON(execution.metadata)}
                  maxHeight="300px"
                  format="json"
                />
              </Box>
            )}

            {/* Expert Results Metadata */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              Expert Results Metadata
            </Typography>
            {execution.expert_results.map((result, idx) => (
              <Accordion key={idx} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">
                    {result.expert_name} - Metadata
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FullscreenTextArea
                    title={`${result.expert_name} - Metadata`}
                    content={formatJSON(result.metadata)}
                    maxHeight="300px"
                    format="json"
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.5 : 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        '& > button': {
          width: isMobile ? '100%' : 'auto'
        }
      }}>
        {/* Show Stop button for START or RUNNING executions */}
        {(execution.status === 'start' || execution.status === 'running') && (
          <Button
            onClick={handleStopExecution}
            disabled={stoppingExecution}
            color="error"
            variant="outlined"
            size={isMobile ? 'medium' : 'medium'}
            startIcon={stoppingExecution ? <CircularProgress size={16} /> : <StopIcon />}
          >
            {stoppingExecution ? 'Stopping...' : (isMobile ? 'Stop' : 'Stop Execution')}
          </Button>
        )}
        {/* Show Retry button for ERROR, COMPLETED, or STOPPED executions */}
        {(execution.status === 'error' || execution.status === 'completed' || execution.status === 'stopped') && (
          <Button
            onClick={handleRetryExecution}
            disabled={retryingExecution}
            color={execution.status === 'error' || execution.status === 'stopped' ? 'warning' : 'primary'}
            variant="outlined"
            size={isMobile ? 'medium' : 'medium'}
            startIcon={retryingExecution ? <CircularProgress size={16} /> : <ReplayIcon />}
          >
            {retryingExecution ? 'Retrying...' : (
              execution.status === 'error' ? (isMobile ? 'Retry' : 'Retry Failed Experts') : 
              execution.status === 'stopped' ? (isMobile ? 'Resume' : 'Resume Execution') : 
              (isMobile ? 'Re-run' : 'Re-run Execution')
            )}
          </Button>
        )}
        <Button onClick={onClose} size={isMobile ? 'medium' : 'medium'}>Close</Button>
      </DialogActions>

      {/* Snackbar for rerun notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ExecutionViewerDialog;
